import MDEditor from '@uiw/react-md-editor';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPrivacyPolicyAndTermOfCondition } from '../../services/Service';

const PrivacyPolicy = () => {
  const [scrolled, setScrolled] = useState(false);
  const [rawData, setRawData] = useState({
    privacyPolicy: '',
    privacyPolicyMD: '',
    privacyPolicyHTML: '',
    termOfCondition: '',
    termOfConditionMD: '',
    termOfConditionHTML: '',
  });

  const fetchData = async () => {
    try {
      const res = await getPrivacyPolicyAndTermOfCondition();
      if (res.status === 200) {
        setRawData(res.data.data);
      }
    } catch (err) {
      console.log('getPrivacyPolicyAndTermOfCondition fetchData error', err.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    :root {
      --primary: #3d71ff;
      --primary-glow: rgba(61, 113, 255, 0.15);
      --primary-dark: #2a56cc;
      --secondary: #00d4ff;
      --text-main: #0f172a;
      --text-muted: #64748b;
      --bg-light: #f8faff;
      --white: #ffffff;
      --glass: rgba(255, 255, 255, 0.8);
      --border: #e2e8f0;
      --shadow-sm: 0 1px 3px rgba(0,0,0,0.12);
      --shadow-md: 0 10px 15px -3px rgba(0,0,0,0.1);
      --shadow-lg: 0 20px 25px -5px rgba(0,0,0,0.05);
      --transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }

    html {
      scroll-behavior: smooth;
    }

    body {
      background-color: var(--white);
      color: var(--text-main);
      line-height: 1.6;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
    }

    .container {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 1.5rem;
    }

    /* Navbar */
    .navbar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 72px;
      display: flex;
      align-items: center;
      z-index: 1000;
      transition: var(--transition);
      background: ${scrolled ? 'var(--glass)' : 'transparent'};
      border-bottom: 1px solid ${scrolled ? 'var(--border)' : 'transparent'};
      backdrop-filter: ${scrolled ? 'blur(16px)' : 'none'};
    }

    .nav-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .logo {
      font-size: 1.25rem;
      font-weight: 800;
      color: var(--text-main);
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.6rem;
      letter-spacing: -0.5px;
    }

    .logo-icon {
      background: var(--primary);
      color: white;
      padding: 6px;
      border-radius: 10px;
      display: flex;
    }

    .nav-links {
      display: flex;
      gap: 2.5rem;
      align-items: center;
    }

    .nav-links a {
      text-decoration: none;
      color: var(--text-muted);
      font-weight: 600;
      font-size: 0.95rem;
      transition: var(--transition);
    }

    .nav-links a:hover {
      color: var(--primary);
    }

    .btn {
      padding: 0.75rem 1.75rem;
      border-radius: 12px;
      font-weight: 700;
      font-size: 0.95rem;
      cursor: pointer;
      transition: var(--transition);
      border: none;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-primary {
      background: var(--primary);
      color: var(--white);
      box-shadow: 0 4px 14px 0 var(--primary-glow);
    }

    .btn-primary:hover {
      background: var(--primary-dark);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px 0 var(--primary-glow);
    }

    /* Hero Section with Enhanced Gradient */
    .hero {
      padding: 200px 0 140px;
      position: relative;
      overflow: hidden;
      text-align: center;
      background: radial-gradient(circle at 50% 0%, rgba(61, 113, 255, 0.08) 0%, rgba(255, 255, 255, 0) 70%),
                  radial-gradient(circle at 100% 100%, rgba(0, 212, 255, 0.05) 0%, rgba(255, 255, 255, 0) 50%),
                  radial-gradient(circle at 0% 100%, rgba(61, 113, 255, 0.05) 0%, rgba(255, 255, 255, 0) 50%);
    }

    /* Decorative Gradient Orbs */
    .hero::before, .hero::after {
      content: '';
      position: absolute;
      width: 600px;
      height: 600px;
      border-radius: 50%;
      filter: blur(120px);
      z-index: -1;
      opacity: 0.4;
    }

    .hero::before {
      top: -200px;
      right: -100px;
      background: var(--primary);
    }

    .hero::after {
      bottom: -100px;
      left: -150px;
      background: var(--secondary);
    }

    .hero-tag {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1.25rem;
      background: var(--white);
      color: var(--primary);
      border: 1px solid var(--border);
      border-radius: 100px;
      font-size: 0.85rem;
      font-weight: 700;
      margin-bottom: 2rem;
      box-shadow: var(--shadow-sm);
    }

    .hero h1 {
      font-size: clamp(2.75rem, 6vw, 4.75rem);
      line-height: 1.05;
      font-weight: 800;
      margin-bottom: 1.5rem;
      color: var(--text-main);
      max-width: 950px;
      margin-left: auto;
      margin-right: auto;
      letter-spacing: -2px;
    }

    .gradient-text {
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .hero p {
      font-size: 1.25rem;
      color: var(--text-muted);
      max-width: 680px;
      margin: 0 auto 3rem;
      font-weight: 500;
    }

    .hero-btns {
      display: flex;
      gap: 1.25rem;
      justify-content: center;
      position: relative;
      z-index: 2;
    }

    /* Grid & Cards */
    .section-padding {
      padding: 120px 0;
    }

    .section-header {
      text-align: center;
      margin-bottom: 5rem;
    }

    .section-header h2 {
      font-size: 2.75rem;
      font-weight: 800;
      margin-bottom: 1.25rem;
      letter-spacing: -1px;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      gap: 2.5rem;
    }

    .card {
      padding: 3rem;
      border-radius: 28px;
      background: var(--white);
      border: 1px solid var(--border);
      transition: var(--transition);
      position: relative;
    }

    .card:hover {
      border-color: transparent;
      box-shadow: var(--shadow-lg);
      transform: translateY(-10px);
    }

    .icon-box {
      width: 60px;
      height: 60px;
      background: var(--primary);
      color: var(--white);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 18px;
      margin-bottom: 2rem;
      box-shadow: 0 8px 16px -4px var(--primary-glow);
    }

    .card h3 {
      margin-bottom: 1.25rem;
      font-size: 1.5rem;
      font-weight: 700;
    }

    /* Feature Split Section */
    .split-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: 6rem;
    }

    .feature-image-container {
      background: var(--white);
      border: 1px solid var(--border);
      border-radius: 32px;
      height: 440px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: var(--shadow-lg);
      position: relative;
      overflow: hidden;
    }

    .feature-list {
      list-style: none;
      margin-top: 2rem;
    }

    .feature-list li {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1.25rem;
      font-weight: 600;
      font-size: 1.1rem;
    }

    .check-icon {
      color: var(--primary);
      background: var(--primary-glow);
      padding: 4px;
      border-radius: 50%;
    }

    /* Footer */
    footer {
      background: #020617;
      color: var(--white);
      padding: 100px 0 50px;
    }

    .footer-grid {
      display: grid;
      grid-template-columns: 1.5fr 1fr 1fr 1fr;
      gap: 4rem;
      margin-bottom: 6rem;
    }

    .footer-col h4 {
      margin-bottom: 1.75rem;
      font-size: 1rem;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: var(--white);
    }

    .footer-links a {
      color: #94a3b8;
      text-decoration: none;
      transition: var(--transition);
      font-weight: 500;
    }

    .footer-links a:hover {
      color: var(--primary);
      padding-left: 4px;
    }

    .copyright {
      padding-top: 2.5rem;
      border-top: 1px solid #1e293b;
      text-align: center;
      color: #64748b;
      font-size: 0.95rem;
    }

    @media (max-width: 1024px) {
      .split-section { grid-template-columns: 1fr; gap: 4rem; text-align: center; }
      .feature-list { display: inline-block; text-align: left; }
      .footer-grid { grid-template-columns: 1fr 1fr; }
    }

    @media (max-width: 768px) {
      .nav-links { display: none; }
      .hero h1 { font-size: 3.25rem; }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav className="navbar">
        <div className="container nav-content">
          <a href="/" className="logo">
            <img src="/img/Logo.png" alt="SafalSubscriptions" />
            {/* <span className="logo-icon">
                  <Zap size={20} fill="white" />
                </span>
                SafalSubscriptions */}
          </a>
          <div className="nav-links">
            <a href="/#features">Features</a>
            <a href="/#benefits">Benefits</a>
            {/* <button className="btn btn-primary">Download App</button> */}
          </div>
          <div />
          <Link to="/login" className="btn btn-primary">
            Login
          </Link>
          {/* <button className="mobile-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X /> : <Menu />}
              </button> */}
        </div>
      </nav>
      <div
        style={{
          borderTop: '1px solid gray',
          padding: '32px',
          margin: '32px',
          marginTop: '80px',
        }}
      >
        <div data-color-mode="light" style={{ overflow: 'auto' }}>
          <MDEditor.Markdown data-color-mode="light" source={rawData.privacyPolicyMD} />
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
